import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Grid,
  useMediaQuery,
  Button,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Autorenew as AutorenewIcon,
  DoneAll as DoneAllIcon,
  Cancel as CancelIcon,
  PhotoCamera as PhotoCameraIcon, // ← ADDED
} from "@mui/icons-material";
import { useTheme, styled } from "@mui/material/styles";
import { toast } from "react-toastify";
import ToolTipTktCont from "../../../../GlobalFunction/ToolTipTktCont";
import { useLayoutEffect, useRef, useState } from "react";

const StyledCard = styled(Card)(({ theme }) => ({
  flexGrow: 1,
  cursor: "pointer",
  borderRadius: "16px",
  background: "#fff",
  border: `2px solid ${theme.palette.grey[300]}`,
  transition: "transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
  "&:hover": {
    transform: "translateY(1px)",
    boxShadow: theme.shadows[2],
    borderColor: theme.palette.primary.light,
  },
  "&:active": {
    transform: "scale(0.98)",
  },
  position: "relative",
  overflow: "hidden",
  "&:before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "radial-gradient(circle, rgba(0,0,0,0.1) 0%, transparent 70%)",
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
  "&:active:before": {
    opacity: 1,
  },
  "&.selected": {
    boxShadow: theme.shadows[8],
  },
}));

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatTime = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getStatusIcon = (status, theme) => {
  switch (status) {
    case "O":
      return <HourglassEmptyIcon sx={{ color: theme.palette.warning.dark, fontSize: "1.2rem" }} />;
    case "I":
      return <AutorenewIcon sx={{ color: "#D32F2F", fontSize: "1.2rem" }} />;
    case "R":
      return <DoneAllIcon sx={{ color: "#FF5722", fontSize: "1.2rem" }} />;
    case "C":
      return <CancelIcon sx={{ color: theme.palette.error.dark, fontSize: "1.2rem" }} />;
    default:
      return null;
  }
};

// Helper to fix invalid URLs like "http:/..." → "http://..."
const fixImageUrl = (url) => {
  if (!url) return null;
  return url.replace(/^http:\/([^/])/, "http://$1");
};

const TaskAcceptanceTable = ({
  rows,
  searchTerm,
  setSearchTerm,
  selectedTicket,
  handleRowClick,
  getStatusChip,
  selectedRows,
  setSelectedRows,
  selectedCategoryId,
  setSelectedCategoryId,
  handleUpdate,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Zoom state
  const [zoomedTicketId, setZoomedTicketId] = useState(null);
  const [zoomedImageUrl, setZoomedImageUrl] = useState(null);

  // Handle image click to toggle zoom
  const handleImageClick = (e, ticketId, imageUrl) => {
    e.stopPropagation();
    if (imageUrl) {
      setZoomedTicketId((prev) => (prev === ticketId ? null : ticketId));
      setZoomedImageUrl((prev) => (prev === imageUrl ? null : imageUrl));
    }
  };

  // Click outside to close zoom
  const handleOutsideClick = (e) => {
    if (zoomedTicketId && e.target.id === "zoom-overlay") {
      setZoomedTicketId(null);
      setZoomedImageUrl(null);
    }
  };

  // 1. FILTER ROWS
  const filteredRows = Array.isArray(rows)
    ? rows.filter((row) => {
        const search = searchTerm.toLowerCase();
        return (
          formatDate(row.TktDate).toLowerCase().includes(search) ||
          formatTime(row.TktDate).toLowerCase().includes(search) ||
          (row.TicketNo && row.TicketNo.toLowerCase().includes(search)) ||
          (row.ServiceName && row.ServiceName.toLowerCase().includes(search)) ||
          (row.CategoryName && row.CategoryName.toLowerCase().includes(search)) ||
          (row.TicketTypeName && row.TicketTypeName.toLowerCase().includes(search)) ||
          (row.TicketServName && row.TicketServName.toLowerCase().includes(search)) ||
          (row.TicketTagName && row.TicketTagName.toLowerCase().includes(search)) ||
          (row.PropName && row.PropName.toLowerCase().includes(search)) ||
          (row.ServiceProvider && row.ServiceProvider.toLowerCase().includes(search)) ||
          getStatusChip(row.TicketSt).props.label.toLowerCase().includes(search)
        );
      })
    : [];

  // 2. DYNAMIC HEIGHT LOGIC
  const containerRef = useRef(null);
  const [dynamicHeight, setDynamicHeight] = useState(300);
  const MAX_HEIGHT = 570;
  const REDUCE_BY = 80;

  const updateHeight = () => {
    const el = containerRef.current;
    if (!el) return;

    let parent = el.parentElement;
    while (parent && window.getComputedStyle(parent).overflow !== "auto" && parent !== document.body) {
      parent = parent.parentElement;
    }

    if (!parent || parent === document.body) {
      const headerHeight = 120;
      const calc = window.innerHeight - headerHeight - REDUCE_BY;
      setDynamicHeight(Math.min(MAX_HEIGHT, Math.max(200, calc)));
      return;
    }

    requestAnimationFrame(() => {
      const parentRect = parent.getBoundingClientRect();
      const selfRect = el.getBoundingClientRect();
      const usedAbove = selfRect.top - parentRect.top;
      const available = parentRect.height - usedAbove - 40;
      const final = Math.max(200, available - REDUCE_BY);
      setDynamicHeight(Math.min(MAX_HEIGHT, final));
    });
  };

  useLayoutEffect(() => {
    updateHeight();
    const ro = new ResizeObserver(updateHeight);
    const el = containerRef.current;
    if (el) {
      let target = el.parentElement;
      while (target && window.getComputedStyle(target).overflow !== "auto" && target !== document.body) {
        target = target.parentElement;
      }
      if (target && target !== document.body) ro.observe(target);
    }

    const handleResize = () => requestAnimationFrame(updateHeight);
    window.addEventListener("resize", handleResize);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, [filteredRows.length]);

  // 3. CARD CLICK LOGIC
  const handleCardClick = (row) => {
    if (selectedRows.length === 0) handleRowClick(row);
  };

  const RowItem = ({ label, value, useTooltip = true }) => (
    <Box sx={{ display: "flex", alignItems: "baseline", mb: 0.5 }}>
      <Typography
        component="span"
        variant="body2"
        sx={{
          fontWeight: 700,
          whiteSpace: "nowrap",
          mr: 0.5,
          fontSize: { xs: "0.99rem", sm: "0.99rem", md: "0.85rem" },
        }}
      >
        {label}:
      </Typography>

      {useTooltip ? (
        <ToolTipTktCont title={value}>{value || "N/A"}</ToolTipTktCont>
      ) : (
        <Typography
          component="span"
          variant="body2"
          sx={{
            fontSize: { xs: "0.99rem", sm: "0.99rem", md: "0.85rem" },
            wordBreak: "break-word",
          }}
        >
          {value || "N/A"}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box sx={{ padding: 2, width: "100%" }}>
      <Box sx={{ width: "100%", mb: 1 }}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Search Tickets"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            sx: {
              borderRadius: "12px",
              backgroundColor: theme.palette.background.paper,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.divider,
              },
              fontSize: "0.85rem",
              height: "40px",
            },
          }}
        />
      </Box>

      <Box ref={containerRef} sx={{ width: "100%" }}>
        {filteredRows.length > 0 ? (
          <Grid
            container
            spacing={2}
            sx={{
              minHeight: `${dynamicHeight}px`,
              display: "flex",
              alignItems: "stretch",
            }}
          >
            {filteredRows.map((row) => {
              const imageUrl = fixImageUrl(row.ImgName);
              const hasImage = !!imageUrl;
              const isZoomed = zoomedTicketId === row.TicketId;

              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={3}
                  key={row.TicketId}
                  sx={{
                    display: "flex",
                    height: "100%",
                  }}
                >
                  <StyledCard
                    className={row.TicketId === selectedTicket?.TicketId ? "selected" : ""}
                    onClick={() => handleCardClick(row)}
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CardContent
                      sx={{
                        padding: "16px !important",
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* HEADER */}
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-start"
                        mb={1}
                        gap={0}
                        flexWrap="nowrap"
                        sx={{ overflow: "hidden", whiteSpace: "nowrap", marginX: { xs: -0.5, sm: 0, md: -0.9 } }}
                      >
                        <Box display="flex" alignItems="center" gap={0.3}>
                          {getStatusIcon(row.TicketSt, theme)}
                          <Typography
                            variant="subtitle1"
                            color="text.primary"
                            fontWeight="bold"
                            sx={{ fontSize: { xs: "1rem", sm: "1rem", md: "0.99rem" } }}
                          >
                            Tkt No. {row.TicketNo}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: { xs: "1rem", sm: "0.85rem", overflow: "visible", whiteSpace: "normal" } }}
                          >
                            {getStatusChip(row.TicketSt)}
                          </Typography>
                          {hasImage && (
                            <PhotoCameraIcon
                              onClick={(e) => handleImageClick(e, row.TicketId, imageUrl)}
                              sx={{
                                fontSize: { xs: "1.5rem", sm: "1.8rem" },
                                color: theme.palette.info.main,
                                cursor: "pointer",
                                ml: 0.5,
                                transition: "transform 0.3s ease-in-out, color 0.3s ease-in-out",
                                transform: isZoomed ? "scale(1)" : "scale(1)",
                                "&:hover": {
                                  transform: "scale(1.1)",
                                  color: theme.palette.info.dark,
                                },
                              }}
                            />
                          )}
                        </Box>
                      </Box>

                      {/* TICKET DETAILS */}
                      {/* <Typography variant="body2" sx={{ mb: 0.5, fontSize: { xs: "0.99rem", sm: "0.99rem", md: "0.85rem" } }}>
                        <span style={{ fontWeight: 700 }}>RaisedBy:</span>{" "}
                        <ToolTipTktCont title={row.Name}>{row.Name || "N/A"}</ToolTipTktCont>
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5, fontSize: { xs: "0.99rem", sm: "0.99rem", md: "0.85rem" } }}>
                        <span style={{ fontWeight: 700 }}>Date:</span> {formatDate(row.TktDate)} |{" "}
                        <span style={{ fontWeight: 700 }}>Time:</span> {formatTime(row.TktDate)}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5, fontSize: { xs: "0.99rem", sm: "0.99rem", md: "0.85rem" } }}>
                        <span style={{ fontWeight: 700 }}>Complain:</span>{" "}
                        <ToolTipTktCont title={row.ServiceName}>{row.ServiceName || "N/A"}</ToolTipTktCont>
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5, fontSize: { xs: "0.99rem", sm: "0.99rem", md: "0.85rem" } }}>
                        <span style={{ fontWeight: 700 }}>Property:</span>{" "}
                        <ToolTipTktCont title={row.PropName}>{row.PropName || "N/A"}</ToolTipTktCont>
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5, fontSize: { xs: "0.99rem", sm: "0.99rem", md: "0.85rem" } }}>
                        <span style={{ fontWeight: 700 }}>Category:</span>{" "}
                        <ToolTipTktCont title={row.CategoryName}>{row.CategoryName || "N/A"}</ToolTipTktCont>
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5, fontSize: { xs: "0.99rem", sm: "0.99rem", md: "0.85rem" } }}>
                        <span style={{ fontWeight: 700 }}>TktFor:</span> {row.TktFor || "N/A"} | {row.BedNo || "N/A"}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5, fontSize: { xs: "0.99rem", sm: "0.99rem", md: "0.85rem" } }}>
                        <span style={{ fontWeight: 700 }}>Priority:</span> {row.TicketServName || "N/A"} |{" "}
                        <span style={{ fontWeight: 700 }}>Tag:</span> {row.TicketTagName || "N/A"}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5, fontSize: { xs: "0.99rem", sm: "0.99rem", md: "0.85rem" } }}>
                        <span style={{ fontWeight: 700 }}>Provider:</span>{" "}
                        <ToolTipTktCont title={row.ServiceProvider}>{row.ServiceProvider || "N/A"}</ToolTipTktCont>
                      </Typography> */}

                      {/* ALL ROWS */}
                      <RowItem label="RaisedBy" value={row.Name} />
                      <RowItem
                        label="Date"
                        value={`${formatDate(row.TktDate)} | ${formatTime(row.TktDate)}`}
                        useTooltip={false}
                      />
                      <RowItem label="Complain" value={row.ServiceName} />
                      <RowItem label="Property" value={row.PropName} />
                      <RowItem label="Category" value={row.CategoryName} />
                      <RowItem
                        label="TktFor"
                        value={`${row.TktFor || "N/A"} | ${row.BedNo || "N/A"}`}
                      />
                      <RowItem
                        label="Priority"
                        value={row.TicketServName}
                        useTooltip={false}
                      />
                      <RowItem
                        label="Tag"
                        value={row.TicketTagName}
                        useTooltip={false}
                      />
                      <RowItem label="Provider" value={row.ServiceProvider} />

                      {/* UPDATE BUTTON */}
                      <Box display="flex" justifyContent="flex-end" mt="auto" sx={{ marginX: { xs: 1, sm: 0 } }}>
                        <Tooltip title="Update ticket" arrow>
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={(e) => {
                              handleUpdate();
                            }}
                            sx={{
                              borderRadius: "12px",
                              textTransform: "none",
                              fontWeight: "bold",
                              fontSize: { xs: "0.99rem", sm: "0.85rem" },
                              height: { xs: "28px", sm: "30px" },
                              minWidth: { xs: "60px", sm: "70px" },
                              padding: { xs: "4px 8px", sm: "4px 10px" },
                            }}
                          >
                            Update
                          </Button>
                        </Tooltip>
                      </Box>
                    </CardContent>
                  </StyledCard>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Box
            sx={{
              height: `${dynamicHeight}px`,
              minHeight: { xs: 200, sm: 300 },
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              border: "1px dashed #ccc",
              borderRadius: "12px",
              mt: 2,
            }}
          >
            <Typography variant="body1" color="text.secondary">
              No Records Found
            </Typography>
          </Box>
        )}
      </Box>

      {/* Full-Screen Zoom Image Overlay */}
      {zoomedTicketId && zoomedImageUrl && (
        <Box
          id="zoom-overlay"
          onClick={handleOutsideClick}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1200,
          }}
        >
          <Box
            component="img"
            src={zoomedImageUrl}
            alt="Zoomed Ticket"
            onError={(e) => {
              e.target.src = "https://via.placeholder.com/600x400?text=Image+Not+Found";
            }}
            sx={{
              maxWidth: { xs: "90vw", sm: "70vw", md: "70vw" },
              maxHeight: { xs: "90vh", sm: "70vh", md: "70vh" },
              objectFit: "contain",
              borderRadius: 2,
              boxShadow: 3,
              transition: "all 0.3s ease-in-out",
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default TaskAcceptanceTable;